import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { RichTextEditorComponent } from '../../../../shared/components/rich-text-editor/rich-text-editor.component';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { MeetingEmailTemplateApiService } from '../../services/meeting-email-template-api.service';
import { MeetingBooksLinkApiService } from '../../services/meeting-books-link-api.service';
import { TemplateApiService } from '../../../templates/services/template-api.service';
import { AvailableVariable, AvailableVariableNamespace } from '../../../templates/models/template.model';
import {
  EMAIL_TEMPLATE_TYPE_LABELS,
  EMAIL_TEMPLATE_TYPE_USAGE,
  EmailTemplateType,
  EmailTemplateTypeUsage,
  MeetingBooksLinkResponse,
  MeetingEmailTemplateRequest,
  MeetingEmailTemplateResponse,
} from '../../models/meeting.model';

@Component({
  selector: 'app-config-email-templates-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    RichTextEditorComponent,
  ],
  templateUrl: './email-templates-form.component.html',
})
export class EmailTemplatesFormComponent implements OnInit {
  @Input() drawerMode = false;

  @Output() saved = new EventEmitter<MeetingEmailTemplateResponse>();
  @Output() cancelled = new EventEmitter<void>();

  /** INC 65: editor WYSIWYG del cuerpo — se vacía de forma síncrona antes de guardar. */
  @ViewChild(RichTextEditorComponent) bodyEditor?: RichTextEditorComponent;
  @ViewChild('subjectInput') subjectInput?: ElementRef<HTMLInputElement>;

  form!: FormGroup;
  isEditing = false;
  id: number | null = null;
  loading = false;
  saving = false;
  error = '';

  readonly types: EmailTemplateType[] = [
    'confirmation_invitee', 'confirmation_host',
    'reminder_invitee', 'reminder_host',
    'followup', 'no_show', 'reschedule', 'cancellation',
  ];

  /** INC 64: páginas de reserva para el selector legible. */
  bookingLinks: MeetingBooksLinkResponse[] = [];
  /**
   * INC 65: merge tags de la REUNIÓN (fecha, hora, enlace, lugar, organizador…).
   * Vienen de `/api/meeting-email-templates/variables`, que expone exactamente lo que
   * el renderizador sustituye al enviar. Antes se pedía el catálogo de marketing
   * (`/api/templates/available-variables`), cuyas variables NO se resuelven en este
   * flujo: el editor ofrecía tags que llegaban crudos al correo y ninguno con datos
   * de la reunión.
   */
  variableNamespaces: AvailableVariableNamespace[] = [];
  /** INC 65: subida de imágenes al cuerpo (mismo almacén que las plantillas de marketing). */
  readonly bodyImageUploadHandler = (file: File): Promise<string> =>
    this.templateAssetApi.uploadAsset(file).toPromise().then((res) => res!.url);

  constructor(
    private fb: FormBuilder,
    private api: MeetingEmailTemplateApiService,
    private bookingLinkApi: MeetingBooksLinkApiService,
    private templateAssetApi: TemplateApiService,
    private authService: AuthService,
    private toast: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.form = this.fb.group({
      templateType: ['confirmation_invitee', Validators.required],
      name: ['', [Validators.required, Validators.maxLength(200)]],
      subject: ['', [Validators.required, Validators.maxLength(500)]],
      bodyHtml: ['', Validators.required],
      bodyText: [''],
      idBookingLink: [null],
      isDefault: [false],
      isActive: [true],
    });
  }

  ngOnInit(): void {
    this.loadBookingLinks();
    this.loadVariables();
    if (this.drawerMode) {
      // En drawer mode el wrapper invoca reset()/loadTemplate() al abrir.
      return;
    }
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.id = +id;
      this.load(this.id);
    }
  }

  private loadBookingLinks(): void {
    this.bookingLinkApi
      .filter({ businessId: this.businessId })
      .pipe(catchError(() => of([] as MeetingBooksLinkResponse[])))
      .subscribe((links) => (this.bookingLinks = links));
  }

  private loadVariables(): void {
    this.api
      .getVariables()
      .pipe(catchError(() => of([] as AvailableVariableNamespace[])))
      .subscribe((ns) => (this.variableNamespaces = ns));
  }

  /** INC 65: todas las variables en una sola lista, para la ayuda debajo del editor. */
  get allVariables(): AvailableVariable[] {
    return this.variableNamespaces.flatMap((ns) => ns.variables);
  }

  /**
   * INC 65 (2ª vuelta): el Asunto es un input plano, así que el botón de variables del
   * editor no lo alcanza. Se recuerda el caret para insertar donde estaba el cursor.
   */
  private subjectCaret: number | null = null;

  rememberSubjectCaret(): void {
    this.subjectCaret = this.subjectInput?.nativeElement.selectionStart ?? null;
  }

  insertVariableEnAsunto(key: string): void {
    const control = this.form.get('subject');
    if (!control) return;
    const tag = `{{${key}}}`;
    const current: string = control.value ?? '';
    const pos = this.subjectCaret ?? current.length;
    const next = current.slice(0, pos) + tag + current.slice(pos);
    control.setValue(next);
    control.markAsDirty();
    this.subjectCaret = pos + tag.length;
    const el = this.subjectInput?.nativeElement;
    if (el) {
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(this.subjectCaret!, this.subjectCaret!);
      });
    }
    this.toast.success({ title: 'Variable insertada', message: `Se agregó ${tag} al asunto.` });
  }

  /** INC 64: etiqueta en español del tipo de plantilla. */
  typeLabel(t: EmailTemplateType | string): string {
    return EMAIL_TEMPLATE_TYPE_LABELS[t as EmailTemplateType] ?? t;
  }

  /** INC 80: cuándo se envía realmente el tipo seleccionado (o si aún no se envía). */
  get tipoSeleccionado(): EmailTemplateTypeUsage | null {
    const t = this.form.get('templateType')?.value as EmailTemplateType | null;
    return t ? EMAIL_TEMPLATE_TYPE_USAGE[t] ?? null : null;
  }

  bookingLinkLabel(link: MeetingBooksLinkResponse): string {
    return link.title || link.slug || `#${link.id}`;
  }

  reset(): void {
    this.isEditing = false;
    this.id = null;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.form.reset({
      templateType: 'confirmation_invitee',
      name: '',
      subject: '',
      bodyHtml: '',
      bodyText: '',
      idBookingLink: null,
      isDefault: false,
      isActive: true,
    });
  }

  loadTemplate(id: number): void {
    this.isEditing = true;
    this.id = id;
    this.load(id);
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar plantilla' : 'Nueva plantilla';
  }

  private load(id: number): void {
    this.loading = true;
    this.api.getById(id, this.businessId).subscribe({
      next: (t) => {
        this.form.patchValue(t);
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar';
        this.loading = false;
      },
    });
  }

  guardar(): void {
    // INC 65: forzar la emisión del contenido del editor antes de leer el form.
    this.bodyEditor?.flushForSave();
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = '';
    const v = this.form.value;
    const body: MeetingEmailTemplateRequest = {
      businessId: this.businessId,
      templateType: v.templateType,
      name: v.name.trim(),
      subject: v.subject,
      bodyHtml: v.bodyHtml,
      bodyText: v.bodyText?.trim() || undefined,
      idBookingLink: v.idBookingLink || undefined,
      isDefault: v.isDefault,
      isActive: v.isActive,
    };

    const op = this.isEditing && this.id
      ? this.api.update(this.id, this.businessId, body)
      : this.api.create(body);

    op.subscribe({
      next: (item) => {
        this.saving = false;
        if (this.drawerMode) {
          this.saved.emit(item);
        } else {
          this.router.navigate(['/meeting/config/email-templates']);
        }
      },
      error: (err) => {
        this.saving = false;
        this.error = err.error?.message || 'Error al guardar';
      },
    });
  }

  cancelar(): void {
    if (this.drawerMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/meeting/config/email-templates']);
    }
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }
}
